import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Aviso, Botao, Campo } from '../componentes/forma.tsx';
import { Caetech, Marca, useClinica } from '../componentes/Marca.tsx';
import { api, ErroApi } from '../lib/api.ts';
import { dataBr, dataBrParaIso, mascaraData } from '../lib/formato.ts';
import { aplicar, temaSalvo } from '../lib/tema.ts';
import './consulta.css';

/* Tela pública de retirada de exame.
   O público aqui é o paciente, não a clínica: campos grandes, uma coisa por
   vez, e nenhum jargão do sistema. Boa parte de quem usa isto vai estar no
   celular, na saída da consulta, com o comprovante de papel na mão. */

interface ExamePublico {
  codigo: number;
  exame: string;
  data: string;
  pronto: boolean;
  /** PRONTO_SEM_LAUDO: exame que a clínica não lauda, já com a imagem. */
  situacao: 'PRONTO' | 'PRONTO_SEM_LAUDO' | 'AGUARDANDO_EXAME' | 'AGUARDANDO_LAUDO';
  token: string | null;
}

interface Resultado {
  paciente: string;
  /** Todos os exames do paciente, do mais recente para o mais antigo. */
  itens: ExamePublico[];
}

const RECADO: Record<ExamePublico['situacao'], string> = {
  PRONTO: '',
  PRONTO_SEM_LAUDO: '',
  AGUARDANDO_EXAME:
    'A radiografia ainda não chegou ao sistema. Se você já fez o exame, aguarde algumas horas.',
  AGUARDANDO_LAUDO:
    'A radiografia já está no sistema e o médico ainda vai preparar o laudo. Volte aqui mais tarde.',
};

export function Consulta() {
  // O código pode vir do link curto que o paciente recebeu por WhatsApp; a data
  // de nascimento continua sendo digitada, e é ela que protege o documento.
  const { codigo: doLink } = useParams<{ codigo: string }>();
  const [codigo, setCodigo] = useState(() => (doLink ?? '').replace(/\D/g, ''));
  /* A data se DIGITA, e o que está aqui é o texto com as barras -- '14/10/1972'.
     Era `type="date"`, que no celular abre o seletor de rolagem: para chegar a
     1972 o paciente rola ano por ano, e isso faz gente desistir na saída da
     consulta. Digitar oito números é mais rápido para quem sabe a própria data
     de nascimento, que é todo mundo. O ISO só é montado na hora de enviar. */
  const [nascimento, setNascimento] = useState('');
  const nascimentoIso = dataBrParaIso(nascimento);
  const [erro, setErro] = useState('');
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [buscando, setBuscando] = useState(false);

  const clinica = useClinica();

  /* Esta tela é sempre clara, e o seletor de tema saiu dela.
     Quem chega aqui é o paciente, uma vez só, vindo de um link no celular --
     não vai escolher tema, e o escuro no meio da rua atrapalha mais do que
     ajuda. Quem trabalha na clínica continua escolhendo: o tema de quem entrou
     é devolvido ao sair daqui, inclusive ao clicar em "Acesso da clínica". */
  useEffect(() => {
    aplicar('claro');
    return () => aplicar(temaSalvo());
  }, []);

  async function buscar(e: FormEvent) {
    e.preventDefault();
    setErro('');
    setCampos({});
    setResultado(null);
    setBuscando(true);
    try {
      setResultado(
        await api.post<Resultado>('/publico/consultar', { codigo, nascimento: nascimentoIso }),
      );
    } catch (e) {
      if (e instanceof ErroApi) {
        setErro(e.message);
        setCampos(e.campos);
      } else {
        setErro('Não foi possível consultar agora. Tente de novo em instantes.');
      }
    } finally {
      setBuscando(false);
    }
  }

  function novaConsulta() {
    setResultado(null);
    setCodigo('');
    setNascimento('');
    setErro('');
  }

  return (
    <div className="consulta">
      <header className="consulta__topo">
        <div className="consulta__marca-central">
          <Marca tamanho="md" subtitulo="Resultados Exames Radiografia" />
        </div>
        <div className="consulta__acoes">
          <Link className="botao botao--neutro botao--md consulta__acesso" to="/entrar">
            Acesso da clínica
          </Link>
        </div>
      </header>

      <main className="consulta__miolo" id="conteudo">
        {!resultado ? (
          <section className="consulta__cartao cartao">
            <h1 className="consulta__titulo">Retire seu exame</h1>
            <p className="consulta__apoio">
              Informe o número do exame que está no seu comprovante e a sua data de
              nascimento.
            </p>

            <form className="consulta__forma" onSubmit={(e) => void buscar(e)} noValidate>
              {erro && <Aviso>{erro}</Aviso>}

              <Campo
                rotulo="Número do exame"
                inputMode="numeric"
                autoComplete="off"
                autoFocus={!codigo}
                placeholder="60473"
                value={codigo}
                erro={campos.codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
              />
              {/* `inputMode="numeric"` é o que faz o celular abrir o teclado
                  de números direto. `autoComplete="bday"` deixa o próprio
                  aparelho oferecer a data, para quem a tem guardada.

                  O erro só aparece depois de oito dígitos: reclamar da data no
                  terceiro número é reclamar de algo que a pessoa ainda está
                  escrevendo. */}
              <Campo
                rotulo="Sua data de nascimento"
                inputMode="numeric"
                autoComplete="bday"
                autoFocus={!!codigo}
                placeholder="14/10/1972"
                maxLength={10}
                value={nascimento}
                erro={
                  campos.nascimento ??
                  (nascimento.length === 10 && !nascimentoIso ? 'Essa data não existe.' : undefined)
                }
                dica="Dia, mês e ano — só os números."
                onChange={(e) => setNascimento(mascaraData(e.target.value))}
              />

              <Botao
                type="submit"
                variante="primario"
                carregando={buscando}
                disabled={!codigo || !nascimentoIso}
              >
                {buscando ? 'Procurando…' : 'Consultar'}
              </Botao>
            </form>

            <p className="consulta__ajuda">
              Não encontrou ou perdeu o comprovante? Ligue para a clínica
              {clinica.data?.telefone ? `: ${clinica.data.telefone}` : '.'}
            </p>
          </section>
        ) : (
          <section className="consulta__cartao cartao" aria-live="polite">
            <p className="consulta__achado">
              {resultado.itens.length === 1
                ? 'Encontramos o seu exame'
                : `Encontramos ${resultado.itens.length} exames seus`}
            </p>
            <p className="consulta__paciente">{resultado.paciente}</p>

            {/* Todos os exames do paciente, e não só o do código digitado: quem
                chega aqui quer levar o que fez na clínica, e guardar um
                comprovante por exame não é razoável. */}
            <ul className="consulta__lista">
              {resultado.itens.map((e) => (
                <li className="consulta__item" key={e.codigo}>
                  <div className="consulta__item-topo">
                    <span className="consulta__item-data">{dataBr(e.data)}</span>
                    <span className="consulta__item-codigo mono">nº {e.codigo}</span>
                  </div>
                  <p className="consulta__item-exame">{e.exame}</p>

                  {e.pronto ? (
                    <a
                      className="botao botao--primario consulta__baixar"
                      href={`/api/publico/laudo?t=${encodeURIComponent(e.token!)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {/* Nomear pelo que o arquivo é: quem pediu só a imagem
                          procuraria por "laudo" e não acharia, ou baixaria
                          esperando um laudo que não existe. */}
                      {e.situacao === 'PRONTO_SEM_LAUDO'
                        ? 'Baixar exame em PDF'
                        : 'Baixar o laudo em PDF'}
                    </a>
                  ) : (
                    <p className="consulta__item-espera">{RECADO[e.situacao]}</p>
                  )}
                </li>
              ))}
            </ul>

            <p className="consulta__ajuda">
              Cada link vale por 20 minutos — depois disso é só consultar de novo.{' '}
              {/* Prometer "laudo assinado" em lista que pode ter exame sem laudo faria
                  o paciente abrir o arquivo procurando um texto que não existe. */}
              {resultado.itens.some((e) => e.situacao === 'PRONTO_SEM_LAUDO')
                ? 'O arquivo traz as imagens e, quando há laudo, o laudo assinado.'
                : 'O arquivo traz o laudo assinado e as imagens.'}
            </p>

            <Botao variante="neutro" onClick={novaConsulta} className="consulta__outra">
              Consultar outro exame
            </Botao>
          </section>
        )}
      </main>

      {clinica.data && (
        <footer className="consulta__rodape">
          <span>
            {clinica.data.nome} — {clinica.data.endereco}
            {clinica.data.telefone ? ` — ${clinica.data.telefone}` : ''}
          </span>
          <Caetech />
        </footer>
      )}
    </div>
  );
}
